// tracks.js — build a full playable tracklist for an artist.
// Founder plain-ink 2026-02-15: "most of them have 4 available, they should've
// all been complete albums." Hand-authored artist.tracks stays first (curator
// order); every additional mp3 on disk that shares the artist's slug-prefix is
// merged in with a slug-derived title. Everything then filtered against the
// manifest so unplayable files never render.

function prefixForArtist(artist) {
  const heroSlug =
    (artist.tracks && artist.tracks[0]?.slug) ||
    artist.heroTrack?.slug ||
    "";
  if (heroSlug) {
    // Strip the first version/variant token — v14, v15, scout_v3, s1, a1, etc.
    // Keep everything before it as the band's disk prefix.
    const stop = heroSlug.match(
      /_(?:v\d+|scout\w*|s\d+|a\d+|final|full|open|main|red)(?=_|$)/i
    );
    if (stop && stop.index > 0) return heroSlug.slice(0, stop.index);
  }
  // Fallback — artist.slug with dashes → underscores.
  return artist.slug.replace(/-/g, "_");
}

function prettyTitle(slug, prefix) {
  let rest = slug.startsWith(prefix + "_") ? slug.slice(prefix.length + 1) : slug;
  // Pull the version tag out for the label suffix.
  const versionMatch = rest.match(/^(v\d+|scout(?:_v\d+)?|s\d+|a\d+)_(.+)$/i);
  let versionSuffix = "";
  if (versionMatch) {
    versionSuffix = ` (${versionMatch[1]})`;
    rest = versionMatch[2];
  }
  const title = rest
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return title + versionSuffix;
}

/**
 * Merge hand-authored artist.tracks with every on-disk mp3 sharing the band's
 * slug prefix. Returns tracks that exist in `availableSet` when the manifest is
 * loaded; falls back to raw hand-authored tracks when the manifest is null.
 */
export function buildTracklist(artist, availableSet) {
  const raw = artist.tracks || [];
  if (!availableSet || !(availableSet instanceof Set)) return raw;

  const prefix = prefixForArtist(artist);
  const known = new Set(raw.map((t) => t.slug));

  const extras = [];
  availableSet.forEach((s) => {
    if (s.startsWith(prefix + "_") && !known.has(s)) {
      extras.push({ slug: s, title: prettyTitle(s, prefix) });
    }
  });
  extras.sort((a, b) => a.slug.localeCompare(b.slug));

  const merged = [...raw, ...extras];
  return merged.filter((t) => availableSet.has(t.slug));
}
